import React from "react";
import PropTypes from "prop-types";
//Material imports
import makeStyles from "@mui/styles/makeStyles";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import Grow from "@mui/material/Grow";
//Limitless imports
import AnimatedFab from "./AnimatedFab";
import ExpandableFab from "./ExpandableFab";
const CustomerFabButton = ({
  isLLMVisible,
  isMobileDevice,
  onClick,
  unReadMessage,
  getCustomOffsetStyles,
  isAppOpen,
  animation,
  fabMobileText,
  clientContextFabText,
  fabButtonStyle,
  expandableFab,
  rightOffset,
  bottomOffset,
  CloseIcon
}) => {
  let fabText;
  if (isMobileDevice && fabMobileText) {
    fabText = fabMobileText;
  }
  if (!isMobileDevice && clientContextFabText) {
    fabText = clientContextFabText;
  }
  const classes = useStyles({
    fabText,
    fabButtonStyle,
    bottomOffset,
    rightOffset
  });
  const [showFabText, setShowFabText] = React.useState(false);
  const fabIcon = isLLMVisible && CloseIcon ? /*#__PURE__*/React.createElement(CloseIcon, {
    className: classes.chatIcon
  }) : /*#__PURE__*/React.createElement(ChatBubbleOutline, {
    className: classes.chatIcon
  });
  const renderFABBadge = (fab, animated = false) => {
    if (unReadMessage && !isLLMVisible) {
      const badgeClasses = `${classes.badgeRoot} ${animated ? classes.animatedBadge : ""}`;
      const offsets = getCustomOffsetStyles && getCustomOffsetStyles(42, animated ? 6 : -17);
      return /*#__PURE__*/React.createElement(React.Fragment, null, fab, /*#__PURE__*/React.createElement("div", {
        className: badgeClasses,
        style: offsets
      }));
    }
    return fab;
  };
  if (fabText) {
    const FabTextComponent = expandableFab ? ExpandableFab : AnimatedFab;
    const animatedFab = /*#__PURE__*/React.createElement(FabTextComponent, {
      classes: classes,
      isMobileDevice: isMobileDevice,
      onClick: onClick,
      fabText: fabText,
      CloseIcon: CloseIcon,
      openIcon: fabIcon,
      color: "primary",
      "aria-label": "Open Live Messenger",
      component: "div",
      disableRipple: true,
      isAppOpen: isAppOpen,
      animation: animation
    });
    return renderFABBadge(animatedFab, true);
  }
  return /*#__PURE__*/React.createElement(Grow, {
    in: true,
    timeout: 600,
    appear: animation
  }, /*#__PURE__*/React.createElement("div", {
    "aria-label": "Open Live Messenger",
    className: `${classes.button} ${classes.fabRoot} ${isMobileDevice && isLLMVisible ? classes.hidden : ""} ${classes.iconButton}`,
    onClick: onClick,
    id: "fabOpenLiveMessenger",
    onMouseOver: () => setShowFabText(true),
    onMouseOut: () => setShowFabText(false)
  }, fabText && showFabText && /*#__PURE__*/React.createElement("span", {
    className: classes.fabText
  }, " ", fabText, " "), renderFABBadge(fabIcon)));
};

/** styles for both fab and animated fab as many are shared */
const useStyles = makeStyles(theme => {
  var _;
  const fabStyles = {
    button: {
      margin: 8,
      position: "fixed",
      bottom: ({
        bottomOffset
      }) => bottomOffset ? bottomOffset : 0,
      right: ({
        rightOffset
      }) => rightOffset ? rightOffset : 0,
      zIndex: "21400053001",
      textTransform: "none",
      height: 48,
      cursor: "pointer",
      borderRadius: ({
        fabText
      }) => !fabText ? "50%" : 24,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    iconButton: {
      width: ({
        fabButtonStyle
      }) => fabButtonStyle === "flat" ? 46 : 48,
      height: ({
        fabButtonStyle
      }) => fabButtonStyle === "flat" ? 46 : 48
    },
    fabText: {
      padding: "0 22px",
      whiteSpace: "nowrap",
      color: theme.palette.primary.contrastText,
      textAlign: "center",
      minWidth: 50,
      fontFamily: theme.typography.fontFamily
    },
    chatIcon: {
      transform: "scaleX(-1)",
      color: theme.palette.primary.contrastText
    },
    hidden: {
      visibility: "hidden"
    },
    badgeRoot: {
      top: -5,
      height: 15,
      width: 15,
      borderRadius: 15,
      position: "absolute",
      zIndex: "21400053002",
      minWidth: "0 !important",
      backgroundColor: "#EF5350"
    },
    "@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)": {
      badgeRoot: {
        top: "auto",
        bottom: 20 + 14,
        right: Number((_ = "20") === null || _ === void 0 ? void 0 : _.replace("px", "")) + 28,
        position: "fixed"
      }
    },
    animatedBadge: {
      top: "unset",
      position: "fixed"
    },
    fabRoot: {
      backgroundColor: theme.palette.primary.main,
      "&:hover": {
        backgroundColor: theme.palette.primary.dark
      }
    }
  };
  let fabBorderColor = theme.palette.contrast.main;
  if (theme.palette.fab) {
    fabStyles.fabRoot = {
      backgroundColor: theme.palette.fab.backgroundColor,
      "&:hover": {
        backgroundColor: theme.palette.fab.hoverBackgroundColor
      },
      "&:active": {
        boxShadow: "unset"
      }
    };
    fabBorderColor = theme.palette.fab.borderColor;
    //override
    if (theme.palette.fab.textColor) {
      fabStyles.fabText.color = theme.palette.fab.textColor;
      fabStyles.chatIcon.color = theme.palette.fab.textColor;
      fabStyles.fabRoot = {
        ...fabStyles.fabRoot,
        border: ({
          fabButtonStyle
        }) => fabButtonStyle === "flat" ? `solid 1px ${fabBorderColor}` : "none"
      };
    }
  }
  return fabStyles;
});
export default CustomerFabButton;
CustomerFabButton.propTypes = {
  isLLMVisible: PropTypes.bool.isRequired,
  isMobileDevice: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  unReadMessage: PropTypes.bool.isRequired,
  getCustomOffsetStyles: PropTypes.func,
  isAppOpen: PropTypes.bool.isRequired,
  animation: PropTypes.bool,
  fabMobileText: PropTypes.string,
  clientContextFabText: PropTypes.string,
  fabButtonStyle: PropTypes.string,
  expandableFab: PropTypes.bool,
  bottomOffset: PropTypes.number,
  rightOffset: PropTypes.string,
  CloseIcon: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.object])
};